/**
 * NgwEditorCursor — `IEditorCursor` 의 NgwEditor 본 구현 (PR-N2).
 *
 * 매핑 전략 (05 §4.5):
 *   - `showCursor` / `focus` → `handle.focus()` (selection 보존 + caret 표시)
 *   - `setIntoNBody` / `setIntoNBodyCore` → `findNodes('nbody')` 로 region 노드 위치 산출 후
 *     `view.dispatch(state.tr.setSelection(...))`. PR-N4 에서 mail-regions extension 이 nbody
 *     노드를 등록하기 전까지는 `Selection.atStart/atEnd(doc)` fallback.
 *   - `moveToValidPos` → 현 selection 의 from/to 가 doc 경계 밖이면 doc 시작으로 보정.
 *   - `isCollapsed` → `view.state.selection.empty`.
 *   - `notifySetDataEnd` → `handler.onSetDataEnd?.()` 위임.
 *   - `getMSReplyStartElement` → Outlook 답장 마커 셀렉터 탐색 후 첫 매칭 element 반환.
 *
 * **DOM context**: `adapter.lifecycle.getHandle()` / `getCore()` 경유. 어댑터 외부 document
 * 직접 접근 금지.
 */

import { Selection } from '@ngweditor/react'
import type { IEditorCursor } from '../IEditorCursor'
import type { NgwEditorAdapter } from './NgwEditorAdapter'

const MS_REPLY_SELECTORS = [
  '.msoNormal',
  '.WordSection1',
  'div[id^="divRplyFwdMsg"]',
  'div[id^="divtagdefaultwrapper"]',
]

export class NgwEditorCursor implements IEditorCursor {
  private readonly adapter: NgwEditorAdapter

  constructor(adapter: NgwEditorAdapter) {
    this.adapter = adapter
  }

  showCursor(): void {
    this.focus()
  }

  setIntoNBody(toStart: boolean, needCallback?: boolean): void {
    this.applyNBodySelection(toStart)
    if (needCallback === true) {
      this.notifySetDataEnd()
    }
  }

  setIntoNBodyCore(toStart?: boolean, _needCallback?: boolean): void {
    this.applyNBodySelection(toStart === true)
  }

  moveToValidPos(): void {
    const core = this.adapter.lifecycle.getCore()
    if (core == null) {
      return
    }
    const view = core.view
    const docSize = view.state.doc.content.size
    const { from, to } = view.state.selection
    if (from < 0 || to > docSize) {
      view.dispatch(view.state.tr.setSelection(Selection.atStart(view.state.doc)))
    }
  }

  isCollapsed(): boolean {
    const core = this.adapter.lifecycle.getCore()
    if (core == null) {
      return true
    }
    return core.view.state.selection.empty
  }

  notifySetDataEnd(): void {
    this.adapter.getHandler()?.onSetDataEnd?.()
  }

  getMSReplyStartElement(): HTMLElement | null {
    const dom = this.adapter.lifecycle.getHandle()?.getEditorDOM()
    if (dom == null) {
      return null
    }
    for (const selector of MS_REPLY_SELECTORS) {
      const found = dom.querySelector<HTMLElement>(selector)
      if (found != null) {
        return found
      }
    }
    return null
  }

  focus(): void {
    // PM view.focus() 로 contenteditable element 에 직접 focus.
    // handle.focus() 만으로는 NgwEditor 의 wrapper element 에 focus 가 가서 PM cursor 표시가
    // 안 되는 경우가 있음 (사용자 보고 2026-05-19 — "Editor 전체 테두리에 focus 표시").
    const core = this.adapter.lifecycle.getCore()
    if (core != null) {
      core.view.focus()
      return
    }
    // core 미준비 fallback — handle.focus 시도
    this.adapter.lifecycle.getHandle()?.focus()
  }

  /**
   * nbody region 노드를 우선 탐색. 없으면 signature/quoted region 직전 위치를 "사용자 본문
   * 마지막" 으로 간주 (toStart === false 일 때). 그것도 없으면 doc 시작/끝 fallback.
   *
   * **fallback 개선 (2026-05-19)**: 새 메일 / 답장 시 nbody region 노드가 PM doc 에 자동 생성되지
   * 않는 시나리오 (EmailForm 이 insertMailLayout 명령을 호출하지 않음) 에서 Selection.atEnd 가
   * doc 의 진짜 끝 = 서명/quoted 다음 으로 cursor 를 보냈다. 사용자 의도 ("서명/quoted 영역을
   * 제외한 본문의 마지막") 와 어긋남. 본 보강은 signature/quoted_header/quoted_body 중 가장
   * 앞쪽 region 의 직전 위치 (즉 사용자 본문 마지막) 로 cursor 를 보낸다.
   */
  private applyNBodySelection(toStart: boolean): void {
    const core = this.adapter.lifecycle.getCore()
    if (core == null) {
      return
    }
    const view = core.view
    const handle = this.adapter.lifecycle.getHandle()

    // 1순위 — nbody region 노드
    const nbodyNodes = handle?.findNodes('nbody') ?? []
    if (nbodyNodes.length > 0) {
      const nbody = nbodyNodes[0]!
      const innerPos = toStart ? nbody.pos + 1 : nbody.pos + nbody.node.nodeSize - 1
      const resolved = view.state.doc.resolve(innerPos)
      view.dispatch(view.state.tr.setSelection(Selection.near(resolved, toStart ? 1 : -1)))
      return
    }

    // 2순위 — toStart === false (본문 끝 위치 요청) 일 때 sentinel region 직전 위치.
    // toStart === true (본문 시작 위치) 는 doc 시작이 의미 동일 — sentinel 우회 불필요.
    if (!toStart) {
      const sentinelRegions: ReadonlyArray<string> = ['signature', 'quoted_header', 'quoted_body']
      let earliestPos: number | null = null
      for (const regionName of sentinelRegions) {
        const found = handle?.findNodes(regionName) ?? []
        for (const entry of found) {
          if (earliestPos === null || entry.pos < earliestPos) {
            earliestPos = entry.pos
          }
        }
      }
      if (earliestPos !== null) {
        const resolved = view.state.doc.resolve(earliestPos)
        view.dispatch(view.state.tr.setSelection(Selection.near(resolved, -1)))
        return
      }
    }

    // 3순위 — 모든 region 부재: doc 시작/끝
    const target = toStart ? Selection.atStart(view.state.doc) : Selection.atEnd(view.state.doc)
    view.dispatch(view.state.tr.setSelection(target))
  }
}
